import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListUl,
  faMusic,
  faPlay,
  faPlus,
  faForward,
  faCheckCircle,
  faTimesCircle,
  faChevronLeft,
  faMinusCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  isSkippedPlaylist,
  areAllTracksSkipped,
  mergeTracksFromSources,
  PLAYING_SESSION_DETAIL_ID,
} from './music_player_context';
import QueueSourceListRow from './queue_source_list_row';

function getCoverTrack(playlist, allTracks) {
  const firstId = playlist?.trackIds?.[0];
  if (!firstId) return null;
  return allTracks.find((t) => t.video_id === firstId) || null;
}

function tracksInPlaylist(playlist, allTracks) {
  if (!playlist?.trackIds?.length) return [];
  return playlist.trackIds
    .map((id) => allTracks.find((t) => t.video_id === id))
    .filter(Boolean);
}

function playingSessionFromSources(sources) {
  const primary = sources?.[0];
  if (!primary) return null;
  const extras = Math.max(0, (sources?.length || 0) - 1);
  const trackIds = [];
  const seen = new Set();
  for (const src of sources) {
    for (const id of src.trackIds || []) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      trackIds.push(id);
    }
  }
  return {
    id: PLAYING_SESSION_DETAIL_ID,
    name: `${primary.name || 'Playing list'}${extras > 0 ? ` +${extras}` : ''}`,
    trackIds,
    isPlayingSession: true,
    sessionLabel: primary.label || 'List',
  };
}

function PlaylistListView({
  ordered,
  allTracks,
  playlists,
  activePlaylistIds,
  appendMode = false,
  onOpenPlaylist,
  onPlayPlaylist,
  onSkipPlaylist,
  onKeepPlaylist,
  onClearSkipped,
}) {
  if (ordered.length === 0) {
    return (
      <div className="music-playlist-empty">
        <FontAwesomeIcon icon={faListUl} />
        <p>Create a playlist from a track’s more menu.</p>
      </div>
    );
  }

  return (
    <ul className="music-playlist-list">
      {ordered.map((playlist) => {
        const cover = getCoverTrack(playlist, allTracks);
        const coverUrl = cover?.thumbnail_medium || cover?.thumbnail_default || '';
        const trackCount = playlist.trackIds?.length || 0;
        const isActive = activePlaylistIds?.has?.(playlist.id);
        const isSkipped = isSkippedPlaylist(playlist);
        const allSkipped = !isSkipped && areAllTracksSkipped(playlist.trackIds || [], playlists);
        const canPlay = !isSkipped && trackCount > 0 && !allSkipped;

        const trailing = isSkipped ? (
          trackCount > 0 && (
            <button
              type="button"
              className="music-playlist-action"
              onClick={(e) => {
                e.stopPropagation();
                onClearSkipped?.();
              }}
              title="Clear Skipped Tracks"
              aria-label="Clear Skipped Tracks"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
              <span>Clear</span>
            </button>
          )
        ) : trackCount > 0 ? (
          <button
            type="button"
            className={`music-playlist-action${allSkipped ? ' is-keep' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (allSkipped) onKeepPlaylist?.(playlist.id);
              else onSkipPlaylist?.(playlist.id);
            }}
            title={allSkipped ? 'Keep' : 'Skip'}
            aria-label={allSkipped ? `Keep all tracks in ${playlist.name}` : `Skip all tracks in ${playlist.name}`}
          >
            <FontAwesomeIcon icon={allSkipped ? faCheckCircle : faForward} />
            <span>{allSkipped ? 'Keep' : 'Skip'}</span>
          </button>
        ) : null;

        const subtitle = isSkipped
          ? (trackCount === 0
            ? 'No skipped tracks'
            : `${trackCount} skipped · Tracks here will be skipped`)
          : [
            `${trackCount} track${trackCount === 1 ? '' : 's'}`,
            allSkipped ? 'All skipped' : null,
            isActive ? 'In queue' : null,
          ].filter(Boolean).join(' · ');

        return (
          <QueueSourceListRow
            key={playlist.id}
            className={[
              isSkipped ? 'is-skipped-playlist' : '',
              allSkipped ? 'is-all-skipped' : '',
            ].filter(Boolean).join(' ')}
            coverUrl={coverUrl}
            name={playlist.name}
            subtitle={subtitle}
            isActive={isActive}
            canPlay={canPlay}
            appendMode={appendMode}
            onPlay={() => onPlayPlaylist?.(playlist.id)}
            onOpen={() => onOpenPlaylist?.(playlist.id)}
            trailing={trailing}
          />
        );
      })}
    </ul>
  );
}

function PlaylistDetailView({
  playlist,
  tracks,
  playlists,
  playTrack,
  loadingTrackId,
  isTrackSkipped,
  activePlaylistIds,
  queueSources = [],
  onRemoveQueueSource,
  onBack,
  onPlayPlaylist,
  appendMode = false,
  onPlayTrack,
  onAddTrack,
  onRemoveTrack,
  onSkipPlaylist,
  onKeepPlaylist,
  onClearSkipped,
  onClose,
  backLabel = 'Playlists',
  isPlayingSession = false,
}) {
  const isSkipped = isSkippedPlaylist(playlist);
  const trackCount = isPlayingSession ? tracks.length : (playlist.trackIds?.length || 0);
  const allSkipped = !isPlayingSession && !isSkipped && areAllTracksSkipped(playlist.trackIds || [], playlists);
  const canPlay = !isSkipped && tracks.length > 0 && !allSkipped;
  const isActive = isPlayingSession || activePlaylistIds?.has?.(playlist.id);
  const queueSource = queueSources.find(
    (s) => s.kind === 'playlist' && s.playlistId === playlist.id
  );
  const coverTrack = tracks[0] || null;
  const coverUrl = coverTrack?.thumbnail_medium || coverTrack?.thumbnail_default || '';

  return (
    <div className="music-playlist-detail">
      <div className="music-playlist-detail-nav">
        <button
          type="button"
          className="music-playlist-back"
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{backLabel}</span>
        </button>
        <button
          type="button"
          className="music-playlist-sheet-done"
          onClick={onClose}
        >
          Done
        </button>
      </div>

      <div className="music-playlist-detail-header">
        <div className={`music-playlist-detail-cover${isSkipped ? ' is-skipped' : ''} music-playlist-thumb-wrap`}>
          <div className="music-playlist-thumb music-playlist-detail-cover-inner">
            <div className="music-playlist-thumb-fallback">
              <FontAwesomeIcon icon={faMusic} />
            </div>
            {coverUrl && <img src={coverUrl} alt="" />}
          </div>
          {canPlay && (
            <button
              type="button"
              className={[
                'music-playlist-thumb-action',
                appendMode ? 'is-add' : 'is-play',
              ].join(' ')}
              onClick={() => onPlayPlaylist?.(playlist.id)}
              aria-label={appendMode ? `Add ${playlist.name} to queue` : `Play ${playlist.name}`}
            >
              <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
            </button>
          )}
        </div>
        <div className="music-playlist-detail-info">
          <h2>{playlist.name}</h2>
          <p>
            {isPlayingSession
              ? `${playlist.sessionLabel || 'List'} · ${trackCount} track${trackCount === 1 ? '' : 's'}`
              : (isSkipped
                ? (trackCount === 0 ? 'No skipped tracks' : `${trackCount} skipped track${trackCount === 1 ? '' : 's'}`)
                : `${trackCount} track${trackCount === 1 ? '' : 's'}${isActive ? ' · In queue' : ''}`)}
          </p>
        </div>
      </div>

      <div className="music-playlist-detail-controls">
        {isPlayingSession ? null : isSkipped ? (
          trackCount > 0 && (
            <button
              type="button"
              className="music-playlist-control-btn"
              onClick={() => onClearSkipped?.()}
            >
              <FontAwesomeIcon icon={faTimesCircle} />
              <span>Clear Skipped Tracks</span>
            </button>
          )
        ) : (
          <>
            {isActive && queueSource && (
              <button
                type="button"
                className="music-playlist-control-btn"
                onClick={() => onRemoveQueueSource?.(queueSource.id)}
              >
                <FontAwesomeIcon icon={faMinusCircle} />
                <span>Remove from queue</span>
              </button>
            )}
            <button
              type="button"
              className={`music-playlist-control-btn${allSkipped ? ' is-keep' : ''}`}
              disabled={trackCount === 0}
              onClick={() => {
                if (allSkipped) onKeepPlaylist?.(playlist.id);
                else onSkipPlaylist?.(playlist.id);
              }}
            >
              <FontAwesomeIcon icon={allSkipped ? faCheckCircle : faForward} />
              <span>{allSkipped ? 'Keep' : 'Skip'}</span>
            </button>
          </>
        )}
      </div>

      {tracks.length === 0 ? (
        <div className="music-playlist-empty music-playlist-empty--compact">
          <FontAwesomeIcon icon={faMusic} />
          <p>{isSkipped ? 'No skipped tracks' : 'No tracks in this playlist'}</p>
        </div>
      ) : (
        <ul className="music-playlist-tracks">
          {tracks.map((track, index) => {
            const skipped = isTrackSkipped?.(track);
            const isPlaying = playTrack?.video_id === track.video_id;
            const isLoading = loadingTrackId === track.video_id;
            const thumb = track.thumbnail_medium || track.thumbnail_default || '';

            return (
              <li
                key={track.video_id || index}
                className={[
                  'music-playlist-track-row',
                  isPlaying ? 'is-playing' : '',
                  skipped ? 'is-skipped' : '',
                ].filter(Boolean).join(' ')}
              >
                <div className="music-playlist-track-main">
                  <span className="music-playlist-track-index" aria-hidden="true">
                    {isLoading ? '…' : index + 1}
                  </span>
                  <button
                    type="button"
                    className="music-playlist-track-art"
                    disabled={!!skipped || isSkipped}
                    onClick={() => {
                      if (skipped || isSkipped) return;
                      if (appendMode && onAddTrack) onAddTrack(track);
                      else onPlayTrack?.(playlist.id, track);
                    }}
                    aria-label={
                      skipped || isSkipped
                        ? `${track.title || 'Track'} (skipped)`
                        : (appendMode
                          ? `Add ${track.title || 'track'} to playing list`
                          : `Play ${track.title || 'track'}`)
                    }
                  >
                    {thumb ? (
                      <img src={thumb} alt="" />
                    ) : (
                      <FontAwesomeIcon icon={faMusic} />
                    )}
                    {!skipped && !isSkipped && (
                      <span className={`music-playlist-track-play${appendMode ? ' is-add' : ''}`} aria-hidden="true">
                        <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="music-playlist-track-text"
                    disabled={!!skipped || isSkipped}
                    onClick={() => {
                      if (skipped || isSkipped) return;
                      onPlayTrack?.(playlist.id, track);
                    }}
                    aria-label={
                      skipped || isSkipped
                        ? `${track.title || 'Track'} (skipped)`
                        : `Play ${track.title || 'track'} now`
                    }
                  >
                    <span className="music-playlist-track-title">
                      {track.title || 'Untitled'}
                    </span>
                    <span className="music-playlist-track-artist">
                      {track.artist || 'Unknown artist'}
                      {skipped ? ' · Skipped' : ''}
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  className={`music-playlist-track-remove${isSkipped ? ' is-keep' : ''}`}
                  onClick={() => onRemoveTrack?.(playlist.id, track.video_id)}
                  title={isSkipped ? 'Keep' : 'Remove from playlist'}
                  aria-label={
                    isSkipped
                      ? `Keep ${track.title || 'track'}`
                      : `Remove ${track.title || 'track'} from playlist`
                  }
                >
                  <FontAwesomeIcon icon={isSkipped ? faCheckCircle : faMinusCircle} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const PlaylistModal = ({
  open,
  playlists = [],
  allTracks = [],
  activePlaylistIds = new Set(),
  queueSources = [],
  appendMode = false,
  playTrack = null,
  loadingTrackId = null,
  isTrackSkipped,
  onPlayPlaylist,
  onPlayTrackFromPlaylist,
  onPlaySessionTrack,
  onAddTrackToQueue,
  onRemoveTrackFromPlaylist,
  onRemoveQueueSource,
  onSkipPlaylist,
  onKeepPlaylist,
  onClearSkipped,
  onClose,
  initialDetailId = null,
}) => {
  const [detailPlaylistId, setDetailPlaylistId] = useState(null);

  useEffect(() => {
    if (!open) {
      setDetailPlaylistId(null);
      return;
    }
    setDetailPlaylistId(initialDetailId ?? null);
  }, [open, initialDetailId]);

  const ordered = useMemo(() => (
    [...playlists].sort((a, b) => {
      const aSkipped = isSkippedPlaylist(a);
      const bSkipped = isSkippedPlaylist(b);
      if (aSkipped !== bSkipped) return aSkipped ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    })
  ), [playlists]);

  const isPlayingSession = detailPlaylistId === PLAYING_SESSION_DETAIL_ID;

  const detailPlaylist = isPlayingSession
    ? playingSessionFromSources(queueSources)
    : (detailPlaylistId == null
      ? null
      : playlists.find((p) => p.id === detailPlaylistId) || null);

  const detailTracks = useMemo(() => {
    if (isPlayingSession) {
      return mergeTracksFromSources(queueSources, allTracks, new Set());
    }
    return detailPlaylist ? tracksInPlaylist(detailPlaylist, allTracks) : [];
  }, [isPlayingSession, queueSources, detailPlaylist, allTracks]);

  // If the open playlist was deleted, fall back to the list
  useEffect(() => {
    if (detailPlaylistId != null && !detailPlaylist) {
      setDetailPlaylistId(null);
    }
  }, [detailPlaylistId, detailPlaylist]);

  if (!open) return null;

  const handleClose = () => {
    setDetailPlaylistId(null);
    onClose?.();
  };

  return (
    <>
      <div
        className="music-playlist-backdrop"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={`music-playlist-sheet${detailPlaylist ? ' music-playlist-sheet--detail' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={detailPlaylist ? detailPlaylist.name : 'Playlists'}
      >
        {detailPlaylist ? (
          <PlaylistDetailView
            playlist={detailPlaylist}
            tracks={detailTracks}
            playlists={playlists}
            playTrack={playTrack}
            loadingTrackId={loadingTrackId}
            isTrackSkipped={isTrackSkipped}
            activePlaylistIds={activePlaylistIds}
            queueSources={queueSources}
            onRemoveQueueSource={onRemoveQueueSource}
            appendMode={appendMode}
            isPlayingSession={isPlayingSession}
            onBack={() => {
              if (initialDetailId != null) handleClose();
              else setDetailPlaylistId(null);
            }}
            backLabel={initialDetailId != null ? 'Music' : 'Playlists'}
            onPlayPlaylist={onPlayPlaylist}
            onPlayTrack={(playlistId, track) => {
              if (isPlayingSession) onPlaySessionTrack?.(track);
              else onPlayTrackFromPlaylist?.(playlistId, track);
            }}
            onAddTrack={onAddTrackToQueue}
            onRemoveTrack={onRemoveTrackFromPlaylist}
            onSkipPlaylist={onSkipPlaylist}
            onKeepPlaylist={onKeepPlaylist}
            onClearSkipped={onClearSkipped}
            onClose={handleClose}
          />
        ) : (
          <>
            <div className="music-playlist-sheet-header">
              <div>
                <h2>Playlists</h2>
                <p>
                  {ordered.length === 0
                    ? 'No playlists yet'
                    : `${ordered.length} playlist${ordered.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                className="music-playlist-sheet-done"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
            <PlaylistListView
              ordered={ordered}
              allTracks={allTracks}
              playlists={playlists}
              activePlaylistIds={activePlaylistIds}
              appendMode={appendMode}
              onOpenPlaylist={setDetailPlaylistId}
              onPlayPlaylist={(id) => {
                onPlayPlaylist?.(id);
                if (!appendMode) handleClose();
              }}
              onSkipPlaylist={onSkipPlaylist}
              onKeepPlaylist={onKeepPlaylist}
              onClearSkipped={onClearSkipped}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PlaylistModal;
